// Container hook for DonationForm — ALL of DonationFormContent's state, refs,
// effects and handlers (one-time + monthly/subscription donation flows, the
// Stripe intent-creation + confirm money paths, express checkout). Extracted
// VERBATIM. The 14 props are what the outer <Elements> wrapper passes.
//
// Decomposed across src/components/payment/useDonationForm/ into:
//   - donationCalculations.js: formatCurrency, calculateTotal (pure)
//   - useProceedToPayment.js: handleProceedToPayment (one-time + monthly
//     payment-intent/subscription creation)
//   - useDonationSubmit.js (+ submitOneTimeDonation/submitSubscriptionConfirm/
//     createDonationSchedule/createForeverSubscription): handleSubmit's
//     validation + its 4 distinct confirm/create branches
// All cross-cutting state (form fields, clientSecret, subscriptionData) stays
// here and is threaded into both sub-hooks, exactly as it was shared between
// the original's inline effects/functions.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logging/logger';
import { formatCurrency, calculateTotal } from './useDonationForm/donationCalculations';
import { useProceedToPayment } from './useDonationForm/useProceedToPayment';
import { useDonationSubmit } from './useDonationForm/useDonationSubmit';

export function useDonationForm({
  onClientSecretChange,
  clientSecretFromParent,
  preservedOneTimeAmount,
  setPreservedOneTimeAmount,
  preservedDonationType,
  setPreservedDonationType,
  preservedMonthlyAmount: preservedMonthlyAmountFromParent,
  setPreservedMonthlyAmount: setPreservedMonthlyAmountFromParent,
  preservedNumberOfPayments: preservedNumberOfPaymentsFromParent,
  setPreservedNumberOfPayments: setPreservedNumberOfPaymentsFromParent,
  preservedIsForever: preservedIsForeverFromParent,
  setPreservedIsForever: setPreservedIsForeverFromParent,
  preservedSubscriptionData: preservedSubscriptionDataFromParent,
  setPreservedSubscriptionData: setPreservedSubscriptionDataFromParent
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  // These hooks will return null if Elements is not rendered yet (which is fine)
  const stripe = useStripe();
  const elements = useElements();

  // Use preserved donation type if available (to prevent reset on remount), otherwise default to 'one-time'
  const [donationType, setDonationType] = useState(preservedDonationType || 'one-time'); // 'one-time' or 'monthly'

  // Update donationType when preservedDonationType changes (from parent)
  useEffect(() => {
    if (preservedDonationType && preservedDonationType !== donationType) {
      setDonationType(preservedDonationType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preservedDonationType]);
  const [oneTimeAmount, setOneTimeAmount] = useState('');
  const [monthlyAmount, setMonthlyAmount] = useState('');
  const [numberOfPayments, setNumberOfPayments] = useState('');
  const [isForever, setIsForever] = useState(false);
  const [firstName, setFirstName] = useState(user?.first_name || '');
  const [lastName, setLastName] = useState(user?.last_name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState('');
  const [donationFor, setDonationFor] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Best Practice: Store paymentIntentId to allow reusing PaymentIntent if checkout is interrupted
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  // Store subscription/subscription schedule data for monthly donations
  // Use preserved subscription data from parent if available (survives remounts)
  const [subscriptionData, setSubscriptionData] = useState(preservedSubscriptionDataFromParent || null);

  // Sync subscriptionData when preserved value changes
  useEffect(() => {
    if (preservedSubscriptionDataFromParent && !subscriptionData) {
      setSubscriptionData(preservedSubscriptionDataFromParent);
    }
  }, [preservedSubscriptionDataFromParent, subscriptionData]);


  // Use preserved values from parent (they survive remounts)
  // These values are stored in the parent component and persist across Elements remounts
  const preservedMonthlyAmount = preservedMonthlyAmountFromParent;
  const preservedNumberOfPayments = preservedNumberOfPaymentsFromParent;
  const preservedIsForever = preservedIsForeverFromParent;

  // Helper functions to set preserved values in parent
  // These update the parent component's state, which survives remounts
  const setPreservedMonthlyAmount = useCallback((value) => {
    setPreservedMonthlyAmountFromParent(value);
  }, [setPreservedMonthlyAmountFromParent]);

  const setPreservedNumberOfPayments = useCallback((value) => {
    setPreservedNumberOfPaymentsFromParent(value);
  }, [setPreservedNumberOfPaymentsFromParent]);

  const setPreservedIsForever = useCallback((value) => {
    setPreservedIsForeverFromParent(value);
  }, [setPreservedIsForeverFromParent]);


  // CRITICAL: Sync form state with preserved values when they become available
  // This ensures inputs display values even if component remounts
  useEffect(() => {
    if (preservedMonthlyAmount && (!monthlyAmount || monthlyAmount === '')) {
      setMonthlyAmount(preservedMonthlyAmount.toString());
    }
  }, [preservedMonthlyAmount, monthlyAmount]);

  useEffect(() => {
    if (preservedNumberOfPayments && preservedNumberOfPayments !== 'forever' && (!numberOfPayments || numberOfPayments === '')) {
      setNumberOfPayments(preservedNumberOfPayments.toString());
    }
  }, [preservedNumberOfPayments, numberOfPayments]);

  useEffect(() => {
    if (preservedIsForever && !isForever) {
      setIsForever(true);
    }
  }, [preservedIsForever, isForever]);

  const [clientSecret, setClientSecret] = useState(clientSecretFromParent || null);
  const [showPaymentElement, setShowPaymentElement] = useState(!!clientSecretFromParent);
  const [showPaymentForm, setShowPaymentForm] = useState(!!clientSecretFromParent);
  const [hasExpressCheckout, setHasExpressCheckout] = useState(false);
  const paymentIntentCreatedRef = useRef(!!clientSecretFromParent);

  // Update local state when parent provides clientSecret
  useEffect(() => {
    if (clientSecretFromParent) {
      // For one-time donations, always show payment element
      if (donationType === 'one-time') {
        setClientSecret(clientSecretFromParent);
        setShowPaymentElement(true);
      }
      // For monthly donations (including forever), show payment element if we have clientSecret
      else if (donationType === 'monthly') {
        setClientSecret(clientSecretFromParent);
        setShowPaymentElement(true);
      }
    } else if (donationType !== 'one-time' && donationType !== 'monthly' && (clientSecret || clientSecretFromParent)) {
      // Clear if we have a clientSecret but switched away from one-time or monthly
      setClientSecret(null);
      setShowPaymentElement(false);
      if (onClientSecretChange) {
        onClientSecretChange(null);
      }
    }
  }, [clientSecretFromParent, donationType, clientSecret, onClientSecretChange]);

  // Also show payment element when we have clientSecret from parent for one-time donations
  useEffect(() => {
    if (clientSecretFromParent && donationType === 'one-time' && !showPaymentElement) {
      setShowPaymentElement(true);
    }
  }, [clientSecretFromParent, donationType, showPaymentElement]);

  // REMOVED: Auto-create payment intent - now only created when user clicks "Proceed to Payment"

  // REMOVED: Auto-create subscription/payment intent - now only created when user clicks "Proceed to Payment"

  // Notify parent when clientSecret changes
  useEffect(() => {
    if (onClientSecretChange && clientSecret) {
      onClientSecretChange(clientSecret);
    }
  }, [clientSecret, onClientSecretChange]);

  // Show payment form when clientSecret becomes available (after "Proceed to Payment" click)
  useEffect(() => {
    if (clientSecret && paymentIntentCreatedRef.current && !showPaymentForm) {
      logger.debug('Client secret became available, showing payment form');
      setShowPaymentForm(true);
      setShowPaymentElement(true);
    }
  }, [clientSecret, showPaymentForm]);

  const { creatingPaymentIntent, handleProceedToPayment } = useProceedToPayment({
    donationType,
    oneTimeAmount,
    setOneTimeAmount,
    preservedOneTimeAmount,
    setPreservedOneTimeAmount,
    monthlyAmount,
    setMonthlyAmount,
    preservedMonthlyAmount,
    setPreservedMonthlyAmount,
    isForever,
    preservedIsForever,
    setPreservedIsForever,
    numberOfPayments,
    preservedNumberOfPayments,
    setPreservedNumberOfPayments,
    firstName,
    lastName,
    email,
    phone,
    donationFor,
    user,
    setError,
    onClientSecretChange,
    setClientSecret,
    setPaymentIntentId,
    setPreservedDonationType,
    paymentIntentCreatedRef,
    setShowPaymentForm,
    setShowPaymentElement,
    setSubscriptionData,
    setPreservedSubscriptionDataFromParent,
  });

  const { handleSubmit } = useDonationSubmit({
    donationType,
    preservedOneTimeAmount,
    oneTimeAmount,
    setOneTimeAmount,
    preservedMonthlyAmount,
    monthlyAmount,
    setMonthlyAmount,
    preservedIsForever,
    isForever,
    setPreservedIsForever,
    preservedNumberOfPayments,
    numberOfPayments,
    setNumberOfPayments,
    setPreservedMonthlyAmount,
    setPreservedNumberOfPayments,
    user,
    firstName,
    lastName,
    email,
    phone,
    donationFor,
    stripe,
    elements,
    clientSecret,
    clientSecretFromParent,
    showPaymentElement,
    paymentIntentId,
    setPaymentIntentId,
    onClientSecretChange,
    setClientSecret,
    setPreservedDonationType,
    preservedDonationType,
    subscriptionData,
    preservedSubscriptionDataFromParent,
    setSubscriptionData,
    setPreservedSubscriptionDataFromParent,
    setShowPaymentElement,
    setPreservedOneTimeAmount,
    navigate,
    setError,
    setSuccess,
    setLoading,
  });

  const totalAmount = calculateTotal({
    donationType,
    preservedOneTimeAmount,
    oneTimeAmount,
    preservedMonthlyAmount,
    monthlyAmount,
    preservedIsForever,
    isForever,
    preservedNumberOfPayments,
    numberOfPayments,
  });


  return {
    clientSecret,
    creatingPaymentIntent,
    donationFor,
    donationType,
    elements,
    email,
    error,
    firstName,
    formatCurrency,
    handleProceedToPayment,
    handleSubmit,
    hasExpressCheckout,
    isForever,
    lastName,
    loading,
    monthlyAmount,
    navigate,
    numberOfPayments,
    oneTimeAmount,
    paymentIntentCreatedRef,
    phone,
    preservedIsForever,
    preservedMonthlyAmount,
    preservedNumberOfPayments,
    setClientSecret,
    setDonationFor,
    setDonationType,
    setEmail,
    setError,
    setFirstName,
    setHasExpressCheckout,
    setIsForever,
    setLastName,
    setMonthlyAmount,
    setNumberOfPayments,
    setOneTimeAmount,
    setPaymentIntentId,
    setPhone,
    setPreservedIsForever,
    setPreservedMonthlyAmount,
    setPreservedNumberOfPayments,
    setShowPaymentElement,
    setShowPaymentForm,
    setSubscriptionData,
    showPaymentElement,
    showPaymentForm,
    stripe,
    success,
    totalAmount,
    user,
  };
}
